<template>
	<App title="Warehouse Dashboard">
        <div class="p-1">

            <!-- ─── Section 1a: Warehouse Details Card ─── -->
            <div class="row g-3 mb-3">
                <div class="col-12">
                    <div class="dash-card">
                        <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">

                            
                            <div class="d-flex align-items-center gap-3">
                                <div class="warehouse-icon-wrap">
                                    <i class="fa fa-warehouse"></i>
                                </div>
                                <div>
                                    <p class="dash-card-label mb-0">Warehouse</p>
                                    <h5 class="dash-title mb-1">{{ warehouse.title }}</h5>
                                    <span :class="['status-badge', warehouse.deleted_at ? 'status-inactive' : 'status-active']">
                                        {{ warehouse.deleted_at ? 'Inactive' : 'Active' }}
                                    </span>
                                </div>
                            </div>


                            <div class="d-flex flex-wrap gap-2">
                           
                                <button class="btn-action btn-action-secondary" @click="editWarehouseProfile">
                                    <i class="fa fa-pen me-2"></i>Edit Warehouse
                                </button>
                                <button class="btn-action btn-action-primary" @click="allocateToStation">
                                    <i class="fa fa-plus me-2"></i>Allocate to Station
                                </button>
                            </div>
                        </div>

                        <hr class="my-3">

                        <div class="info-grid">
                            <div class="info-item">
                                <p class="info-label">Address</p>
                                <p class="info-value">{{ warehouse.address }}</p>
                            </div>
                            <div class="info-item">
                                <p class="info-label">Location</p>
                                <p class="info-value">
                                    <i class="fa fa-map-marker-alt me-1 text-muted"></i>
                                    {{ warehouse.state + ', ' + warehouse.country }}
                                </p>
                            </div>
                            <div class="info-item">
                                <p class="info-label">Zone</p>
                                <p class="info-value">{{ warehouse.zone ?? 'N/A' }}</p>
                            </div>
                            <div class="info-item">
                                <p class="info-label">Cluster</p>
                                <p class="info-value">{{ warehouse.cluster ?? 'N/A' }}</p>
                            </div>
                            <div class="info-item">
                                <p class="info-label">Manager</p>
                                <p class="info-value">{{ warehouse.manager ?? 'Unassigned' }}</p>
                            </div>
                            <div class="info-item">
                                <p class="info-label">Date Created</p>
                                <p class="info-value">{{ formatDate(warehouse.created_at) }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ─── Section 1b: Inventory Summary Cards ─── -->
            <div class="row g-3 mb-4">
                <div class="col-12 col-md-4">
                    <Counter
                        title="Total Inventory"
                        :value="warehouseStocks.length"
                        :growth="null"
                        icon="fa fa-boxes"
                        color="text-primary"
                    />
                </div>
                <div class="col-12 col-md-4">
                    <Counter
                        title="Total Allocations"
                        :value="allocationCounts.total"
                        :growth="null"
                        icon="fa fa-share-square"
                        color="text-success"
                    />
                </div>
                <div class="col-12 col-md-4">
                    <Counter
                        title="Pending Allocation"
                        :value="allocationCounts.pending"
                        :growth="null"
                        icon="fa fa-clock"
                        color="text-warning"
                    />
                </div>
            </div>

            <!-- ─── Section 2: Stock Table ─── -->
            <div class="row g-3 mb-4">
                <div class="col-12">
                    <div class="dash-card">

                        <!-- Table header -->
                        <div class="d-flex align-items-start justify-content-between gap-3 mb-3 flex-wrap">
                            <div>
                                <h5 class="fw-bold text-dark mb-1">Stock Inventory</h5>
                                <p class="text-muted small mb-0">All stock items registered in this warehouse</p>
                            </div>
                            <button class="btn-action btn-action-primary" @click="createStock">
                                <i class="fa fa-plus me-2"></i>Create Stock
                            </button>
                        </div>
                        <!-- Table -->
                        <div class="table-responsive">
                            <Datatable :options="stock_options" :scopes="scope"/>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ─── Section 3: Allocation History ─── -->
            <div class="row g-3" id="allocation-history">
                <div class="col-12">
                    <div class="dash-card">
                        <div class="mb-3">
                            <h5 class="fw-bold text-dark mb-1">Allocation History</h5>
                            <p class="text-muted small mb-0">Stock allocation batches originating from this warehouse</p>
                        </div>

                        <div class="table-responsive">
                            <Datatable :options="batch_options" :scopes ="batch_scope"/>
                        </div>
                    </div>
                </div>
            </div>

        </div>
	</App>
</template>

<script lang="ts">
import type Admin from '@/models/Admin'
import Counter from '@/vue/components/Counter.vue'
import App from '@/vue/templates/App.vue'
import { CellRender, Column } from '@/vue/utils/Datatable'
import { Config } from 'datatables.net-bs5'
import { Component, Vue, toNative, Prop } from 'vue-facing-decorator'
import Datatable from '@/vue/components/Datatable.vue'

interface WarehouseStock {
    id: number
    serial: string
    type: string
    status: 'assigned' | 'unassigned'
    assigned_station: string | null
    created_at: string
    assigned_at: string | null
}

interface WarehouseBatch {
    id: number
    reference: string
    destination: string
    total_items: number
    status: 'approved' | 'pending' | 'declined'
    created_at: string
}

@Component({
	components: {
		App,
		Counter,
        Datatable,
	},
})
class WarehouseDashboard extends Vue {
    stocksLoading  = false
    batchesLoading = false
    warehouseStocks: WarehouseStock[] = []
    batches: WarehouseBatch[] = []

    // stock filter state
    selectedTypes:  string[] = []
    registeredFrom = ''
    registeredTo   = ''
    assignedFrom   = ''
    assignedTo     = ''

    @Prop({ type: Object })
    readonly auth!: Admin

    @Prop({ type: Object })
    readonly warehouse!: Record<string, any>

    allocationCounts = { total: 0, approved: 0, declined: 0, pending: 0 }

    // ── computed ──────────────────────────────────────────────

    get stockFilterLabel() {
        if (!this.selectedTypes.length) return 'Stock Type'
        if (this.selectedTypes.length === 1) return this.selectedTypes[0]
        return `${this.selectedTypes.length} types selected`
    }

    get hasStockFilters() {
        return this.selectedTypes.length > 0
            || !!this.registeredFrom || !!this.registeredTo
            || !!this.assignedFrom   || !!this.assignedTo
    }

    get filteredStocks() {
        return this.warehouseStocks.filter(stock => {
            if (this.selectedTypes.length && !this.selectedTypes.includes(stock.type)) return false

            if (this.registeredFrom && stock.created_at) {
                if (new Date(stock.created_at) < new Date(this.registeredFrom)) return false
            }
            if (this.registeredTo && stock.created_at) {
                if (new Date(stock.created_at) > new Date(this.registeredTo + 'T23:59:59')) return false
            }

            if ((this.assignedFrom || this.assignedTo) && !stock.assigned_at) return false

            if (this.assignedFrom && stock.assigned_at) {
                if (new Date(stock.assigned_at) < new Date(this.assignedFrom)) return false
            }
            if (this.assignedTo && stock.assigned_at) {
                if (new Date(stock.assigned_at) > new Date(this.assignedTo + 'T23:59:59')) return false
            }

            return true
        })
    }

    // ── helpers ───────────────────────────────────────────────

    clearStockFilters() {
        this.selectedTypes  = []
        this.registeredFrom = ''
        this.registeredTo   = ''
        this.assignedFrom   = ''
        this.assignedTo     = ''
    }

    formatDate(date: string | null) {
        if (!date) return '—'
        return new Intl.DateTimeFormat('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
        }).format(new Date(date))
    }

    capitalize(str: string) {
        return str.charAt(0).toUpperCase() + str.slice(1)
    }

    // ── lifecycle ─────────────────────────────────────────────

    mounted() {
        this.loadWarehouseStocks()
        this.loadAllocationCounts()
        this.loadStockTypes()
    }

    // ── data loading ──────────────────────────────────────────

    async loadStockTypes() {
        try {
            const { data } = await this.$http.get(this.route('stock.ajax.filters'))
            this.allStockTypes = data.types ?? []
        } catch (e) {
            console.error('Error loading stock types:', e)
        }
    }

    async loadAllocationCounts() {
        try {
            const { data } = await this.$http.get(this.route('warehouse.ajax.allocation.count', { warehouse: this.warehouse.slug }))
            this.allocationCounts = data.counts
        } catch (e) {
            console.error('Error loading allocation counts:', e)
        }
    }

    async loadWarehouseStocks() {
        this.stocksLoading = true
        try {
            const { data } = await this.$http.get(
                this.route('warehouse.ajax.stocks.all', { warehouse: this.warehouse.slug })
            )
            this.warehouseStocks = data.stocks
        } catch (e) {
            console.error('Error loading stocks:', e)
        } finally {
            this.stocksLoading = false
        }
    }


    // ── actions ───────────────────────────────────────────────

    scrollToHistory() {
        document.getElementById('allocation-history')?.scrollIntoView({ behavior: 'smooth' })
    }

    allocateToStation() {
        this.router.visit(this.route('assignment.page.create'))
    }

    editWarehouseProfile() {
        this.router.visit(this.route('warehouse.page.update', { warehouse: this.warehouse.slug }))
    }

    createStock() {
        this.router.visit(this.route('stock.page.create'))
    }



    allStockTypes: string[] = []

    get scope(): { value: string; label: string }[] {
        const counts = new Map<string, number>()
        for (const stock of this.warehouseStocks) {
            if (stock.type) counts.set(stock.type, (counts.get(stock.type) ?? 0) + 1)
        }
        return this.allStockTypes.map(type => ({
            value: type,
            label: `${type} (${counts.get(type) ?? 0})`,
        }))
    }

    get batch_buttons() {
		let buttons: any[] = []
		buttons.push('refresh')
		buttons.push('trashed')

		return buttons
	}



    get buttons() {
		let buttons: any[] = []
		buttons.push('refresh')
		buttons.push({
			extend: 'create',
			attr: {
				href: this.route('engine.page.create'),
			},
		})
		buttons.push('trashed')

		return buttons
	}

	get columns() {
		let columns: any[] = []

		columns.push({
			data: 'serial',
			title: 'Serial Number',
		})
		
		columns.push({
			data: 'warehouse.title',
			title: 'Assigned Warehouse',
			orderable: true,
			name: 'warehouse',
			
		})
		columns.push({
			data: 'type',
			title: 'Type',
			orderable: true,
			name:'type',
		})
		

		columns.push({
			data: 'created_at',
			title: 'Registered',
			render: CellRender.date(),
			orderable: true,
			name: 'created_at',
		})

		columns.push(Column.actions(this.$gate))

		return columns
	}

	get stock_options() {
		return {
			ajax: {
				url: this.route('warehouse.ajax.stocks', { warehouse: this.warehouse.slug }),
				method: 'GET',
			},
			buttons: this.buttons,
			columns: this.columns,
		}
	}


    batch_scope = ['pending','approved', 'declined']
	get batch_columns() {
		let columns: any[] = []
		
		columns.push({
			data: 'reference',
			title: 'Reference no',
		})
		columns.push({
			data: 'assignments.0.warehouse.title',
			title: 'From',
		})
		columns.push({
			data: 'station.title',
			title: 'To',
		})
		columns.push({
			data: 'quantity',
			title: 'No. of Items',
		})
		columns.push({
			data: 'status',
			title: 'Status',
			
		
		})
	
		columns.push({
			name: 'Date Created',
			data: 'created_at',
			title: 'Date Created',
			render: CellRender.date(),
			orderable: true,
		})
		columns.push(Column.actions(this.$gate))
		return columns
	}

	get batch_options(): Config {
		return {
			ajax: {
				url: this.route('warehouse.ajax.batches', { warehouse: this.warehouse.slug }),
				method: 'GET',
			},
			buttons: this.batch_buttons,
			columns: this.batch_columns,
			order: [[3, 'desc']],
		}
	}
}

export default toNative(WarehouseDashboard)
</script>

<style lang="scss" scoped>
/* ── Typography ─────────────────────────────────────────── */
.dash-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #111827;
    letter-spacing: -0.3px;
}

.dash-card-label {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: #9ca3af;
    text-transform: uppercase;
    margin-bottom: 0.4rem;
}

/* ── Cards ──────────────────────────────────────────────── */
.dash-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 1.25rem 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: box-shadow 0.2s;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }
}

/* ── Warehouse icon ─────────────────────────────────────── */
.warehouse-icon-wrap {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: rgba(245, 158, 11, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
        font-size: 1.25rem;
        color: #d97706;
    }
}

/* ── Status badge ───────────────────────────────────────── */
.status-badge {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.2rem 0.55rem;
    border-radius: 20px;
    letter-spacing: 0.03em;
}

.status-active {
    background: #dcfce7;
    color: #15803d;
}

.status-inactive {
    background: #fee2e2;
    color: #b91c1c;
}

/* ── Info Grid ──────────────────────────────────────────── */
.info-grid {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
}

.info-item {
    flex: 1 1 0;
    min-width: 0;
    padding: 0 1.1rem;
    border-right: 1px solid #f3f4f6;

    &:first-child { padding-left: 0; }
    &:last-child  { border-right: none; padding-right: 0; }

    .info-label {
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        color: #9ca3af;
        text-transform: uppercase;
        margin-bottom: 0.2rem;
        white-space: nowrap;
    }

    .info-value {
        font-size: 0.85rem;
        color: #111827;
        margin-bottom: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

/* ── Action Buttons ─────────────────────────────────────── */
.btn-action {
    border: none;
    padding: 0.45rem 1rem;
    border-radius: 8px;
    font-size: 0.82rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
}

.btn-action-primary {
    background: #3D5A00;
    color: #fff;

    &:hover { background: #3D5A00; }
}

.btn-action-secondary {
    background: #fff;
    color: #3D5A00;
    border: 1px solid #e5e7eb;

    &:hover { background: #f9fafb; }
}

/* ── Table ──────────────────────────────────────────────── */
.table-th {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: #6b7280;
    text-transform: uppercase;
    border-bottom: 1px solid #f3f4f6;
    padding: 0.75rem 1rem;
    white-space: nowrap;
}

.table-td {
    font-size: 0.875rem;
    color: #374151;
    padding: 0.85rem 1rem;
    vertical-align: middle;
    border-bottom: 1px solid #f9fafb;
}

/* ── Stock status badges ────────────────────────────────── */
.stock-badge {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
}

.stock-assigned {
    background: #dbeafe;
    color: #1d4ed8;
}

.stock-unassigned {
    background: #f3f4f6;
    color: #6b7280;
}

/* ── Batch status badges ────────────────────────────────── */
.batch-badge {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
}

.batch-approved {
    background: #dcfce7;
    color: #15803d;
}

.batch-pending {
    background: #fef9c3;
    color: #a16207;
}

.batch-declined {
    background: #fee2e2;
    color: #b91c1c;
}

/* ── View link ──────────────────────────────────────────── */
.btn-link-view {
    font-size: 0.82rem;
    font-weight: 500;
    color: #111827;
    text-decoration: none;
    padding: 0.3rem 0.7rem;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    transition: background 0.15s;

    &:hover {
        background: #f3f4f6;
        color: #111827;
    }
}
</style>
